// Mirrors the SDK session onto an app's root state so components can read
// `root.signedIn`, `root.currentUserId`, and
// `root.jwtClaims.workspace_org_id` without depending on gov-specific
// modules. All three editor apps (canvas, CMS, gov, future consumers)
// point at the same SDK auth pipeline, so subscribing here gives every
// origin the same identity view.
//
// Usage:
//   onCreate: (el, s) => el.call('mirrorAuthSession', s.root || s)
// Returns an unsubscribe fn — call it to tear down; otherwise the
// subscription lives for the lifetime of the page.

import { getSDK } from './sdk.js'

// NOTE: this module used to also export a `hasSessionToken()` that scanned
// localStorage for `sb-<ref>-auth-token` keys — the governance Supabase
// project's session marker. That project was removed, the helper had zero
// callers anywhere in the monorepo, and its name collided with the LIVE
// SDK-token detector at `workspace/functions/auth.js` (both are `export *`-ed
// into overlapping context registries, so the dead Supabase one could shadow
// the real one). Removed 2026-07-27. The canonical signal is
// `hasSessionToken` from `@symbo-ls/workspace-shell/functions/auth.js`.

// Subscribe to SDK auth events and mirror the session onto root state.
// Idempotent — repeat calls reuse the existing subscription via a symbol
// on `root`.
export const mirrorAuthSession = function mirrorAuthSession(root, onSignIn) {
  if (!root || root._authMirrorInstalled) return root?._authMirrorInstalled
  root._authMirrorInstalled = true

  // migrated 2026-04-27 to SDK onAuthStateChange (signature is event, session;
  // matches Supabase's shape so the callback body is unchanged).
  let lastUserId = null
  const unsubscribe = getSDK().onAuthStateChange((_event, session) => {
    const u = session?.user
    const nextEmail = u?.email || ''
    const nextId = u?.id || ''
    const nextSignedIn = !!u
    const nextClaims = u?.app_metadata || {}
    const changed =
      root.signedIn !== nextSignedIn ||
      root.currentUserEmail !== nextEmail ||
      root.currentUserId !== nextId
    if (changed) {
      root.update({
        signedIn: nextSignedIn,
        currentUserEmail: nextEmail,
        currentUserId: nextId,
        jwtClaims: nextClaims
      })
    } else if (nextSignedIn) {
      root.update({ jwtClaims: nextClaims })
    }
    if (nextId && nextId !== lastUserId) {
      lastUserId = nextId
      if (typeof onSignIn === 'function') onSignIn({ id: nextId, email: nextEmail })
    } else if (!nextId) {
      lastUserId = null
    }
  })

  // SDK's onAuthStateChange returns the unsubscribe fn directly. The previous
  // sdk-bridge implementation returned `{ data: { subscription: { unsubscribe } } }`
  // and the wrapper here adapted that shape — keep returning a plain function so
  // callers can invoke the returned unsub directly.
  const unsub = () => {
    if (typeof unsubscribe === 'function') unsubscribe()
  }
  return unsub
}
